import type {
  ClassInfo,
  RecoveredExpression,
} from "./types";

/**
 * Static string recovery: turning encoded constants back into text.
 *
 * This is the obfuscation-facing half of a scan. A packed char array, a
 * rotating XOR call site, a constant Base64 call, an invokedynamic metadata
 * blob, a byte array of literals -- each has its own detector here, and each
 * detector reports through one vocabulary, `staticRecoveredTransform`.
 *
 * It sat inside `analyzer.ts` next to the archive walk and the report
 * assembly, which meant an obfuscation fix was a change to the same 4,000-line
 * module that decides a verdict. The decoders have no dependency on the scan
 * lifecycle, the rules, or the report: they take a class or a candidate string
 * and return text.
 *
 * Two vocabularies meet here and it is worth naming them. The detector
 * vocabulary (`StaticRecoveredString["transform"]`, nine members) is
 * fine-grained; the reporting vocabulary (`RecoveredExpression["transform"]`,
 * eleven members) is what an analyst reads. `staticRecoveredTransform` is the
 * single translation between them, because two copies of that mapping is
 * exactly how a char-packed string inside a nested archive came to be reported
 * as "constant propagation" while the same string at top level was reported as
 * "character-array packing".
 *
 * The block was moved verbatim. No detector, threshold, pattern or transform
 * label changed in this split.
 */

function printableRatio(value: string) {
  if (!value.length) return 0;
  let printable = 0;
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (
      character === "\n" ||
      character === "\r" ||
      character === "\t" ||
      (code >= 32 && code < 127)
    ) {
      printable += 1;
    }
  }
  return printable / value.length;
}

interface StaticRecoveredString {
  value: string;
  transform:
    | "call-site rotating XOR"
    | "constant-call XOR"
    | "constant-call character shift"
    | "constant-call Base64"
    | "heuristic constant-pool XOR"
    | "character-array packing"
    | "invokedynamic metadata XOR"
    | "byte-array string"
    | "static fragment rebuild";
  method?: string;
  methodDescriptor?: string;
  offset?: number;
  sequenceStartOffset?: number;
  patchable?: boolean;
  original: string;
}

function instructionIntegerLiteral(
  instruction: ClassInfo["methods"][number]["instructions"][number],
) {
  if (typeof instruction.literal === "number") return instruction.literal;
  if (instruction.mnemonic === "iconst_m1") return -1;
  const constant = instruction.mnemonic.match(/^[ild]const_(\d)$/)?.[1];
  if (constant) return Number(constant);
  if (instruction.mnemonic === "bipush" || instruction.mnemonic === "sipush") {
    const value = Number.parseInt(instruction.operands, 10);
    return Number.isFinite(value) ? value : null;
  }
  return null;
}

function decodedTextScore(value: string) {
  if (value.length < 2 || value.length > 16_384) return 0;
  let visible = 0;
  let useful = 0;
  const unique = new Set<string>();
  for (const character of value) {
    const code = character.charCodeAt(0);
    const isVisible =
      character === "\n" ||
      character === "\r" ||
      character === "\t" ||
      (code >= 32 && code !== 0x7f && code !== 0xfffd);
    if (isVisible) visible += 1;
    if (
      /[\p{L}\p{N}\s]/u.test(character) ||
      "/\\.:,;_-$@?&=%+{}[]()'\"<>!".includes(character)
    ) {
      useful += 1;
    }
    unique.add(character);
  }
  let score =
    (visible / value.length) * 58 +
    (useful / value.length) * 24 +
    Math.min(8, (unique.size / Math.min(value.length, 24)) * 8);
  if (
    /https?:\/\/|(?:^|[./])(?:com|net|org|dev)\b|minecraft|token|uuid|username|password|webhook|\/api\/|[A-Za-z_$][\w$]*(?:\/[A-Za-z_$][\w$]*){2,}/i.test(
      value,
    )
  ) {
    score += 16;
  } else if (/[A-Za-z]{3,}/.test(value)) {
    score += 7;
  }
  if (/^([\s\S])\1{3,}$/.test(value)) score -= 32;
  return Math.max(0, Math.min(100, score));
}

function xorCharacters(value: string, key: number) {
  return Array.from(value, (character) =>
    String.fromCharCode(character.charCodeAt(0) ^ (key & 0xffff)),
  ).join("");
}

function shiftCharacters(value: string, key: number, direction: 1 | -1) {
  return Array.from(value, (character) =>
    String.fromCharCode(
      (character.charCodeAt(0) + direction * key) & 0xffff,
    ),
  ).join("");
}

function repeatingXorCharacters(value: string, key: string) {
  if (!key.length) return value;
  return Array.from(value, (character, index) =>
    String.fromCharCode(
      character.charCodeAt(0) ^ key.charCodeAt(index % key.length),
    ),
  ).join("");
}

function bestDecodedCandidate(
  original: string,
  candidates: Array<{
    value: string | null;
    transform: StaticRecoveredString["transform"];
  }>,
  provenTransform = false,
) {
  const baseline = decodedTextScore(original);
  return candidates
    .filter(
      (
        item,
      ): item is {
        value: string;
        transform: StaticRecoveredString["transform"];
      } =>
        Boolean(item.value) &&
        item.value !== original &&
        decodedTextScore(item.value as string) >=
          (provenTransform ? 68 : Math.max(74, baseline + 14)),
    )
    .sort(
      (left, right) =>
        decodedTextScore(right.value) - decodedTextScore(left.value),
    )[0];
}

function constantCallRecovery(
  classInfo: ClassInfo,
  method: ClassInfo["methods"][number],
  instructionIndex: number,
) {
  const call = method.instructions[instructionIndex];
  if (
    call.mnemonic !== "invokestatic" ||
    !/→ java\.lang\.String$/.test(call.reference ?? "")
  ) {
    return null;
  }
  const rawArgs = call.reference?.match(/\(([^()]*)\) → java\.lang\.String$/)?.[1];
  if (rawArgs === undefined) return null;
  const argumentTypes = rawArgs
    ? rawArgs.split(",").map((item) => item.trim())
    : [];
  if (argumentTypes.length < 1 || argumentTypes.length > 3) return null;
  const firstArgument = instructionIndex - argumentTypes.length;
  if (firstArgument < 0) return null;
  const argumentInstructions = method.instructions.slice(
    firstArgument,
    instructionIndex,
  );
  const values = argumentInstructions.map((instruction, index) =>
    argumentTypes[index] === "java.lang.String"
      ? typeof instruction.literal === "string"
        ? instruction.literal
        : null
      : instructionIntegerLiteral(instruction),
  );
  if (values.some((value) => value === null)) return null;
  const cipherIndex = argumentTypes.indexOf("java.lang.String");
  if (cipherIndex < 0 || typeof values[cipherIndex] !== "string") return null;
  const cipherText = values[cipherIndex] as string;
  if (cipherText.length < 2 || cipherText.length > 16_384) return null;
  const candidates: Array<{
    value: string | null;
    transform: StaticRecoveredString["transform"];
  }> = [];
  const integerKeys = values.filter(
    (value): value is number => typeof value === "number",
  );
  const stringKeys = values.filter(
    (value, index): value is string =>
      index !== cipherIndex && typeof value === "string",
  );
  const targetName = call.reference
    ?.slice(0, call.reference.indexOf("("))
    .split(".")
    .at(-1);
  const target = targetName
    ? classInfo.methods.find(
        (candidate) =>
          candidate.name === targetName &&
          candidate.descriptor.includes("java.lang.String"),
      )
    : undefined;
  const targetOpcodes = new Set(
    target?.instructions.map((instruction) => instruction.mnemonic) ?? [],
  );
  const nameSignalsDecryptor =
    /decrypt|decode|decipher|unscramble|unpack|xor/i.test(targetName ?? "");
  const hasXorTransform =
    targetOpcodes.has("ixor") ||
    targetOpcodes.has("lxor") ||
    nameSignalsDecryptor;
  const hasShiftTransform =
    targetOpcodes.has("iadd") ||
    targetOpcodes.has("isub") ||
    targetOpcodes.has("ladd") ||
    targetOpcodes.has("lsub");
  const decryptorProven =
    hasXorTransform ||
    hasShiftTransform ||
    /base64|hex/i.test(call.reference ?? "");

  if (
    argumentTypes.join(",") === "int,java.lang.String" &&
    typeof values[0] === "number"
  ) {
    candidates.push({
      value: decodeCallSiteRotatingXor(
        classInfo.name,
        values[0],
        cipherText,
      ),
      transform: "call-site rotating XOR",
    });
  }
  for (const rawKey of integerKeys) {
    const key = rawKey & 0xffff;
    if (hasXorTransform) {
      candidates.push({
        value: xorCharacters(cipherText, key),
        transform: "constant-call XOR",
      });
    }
    if (hasShiftTransform) {
      candidates.push({
        value: shiftCharacters(cipherText, key, -1),
        transform: "constant-call character shift",
      });
      candidates.push({
        value: shiftCharacters(cipherText, key, 1),
        transform: "constant-call character shift",
      });
    }
    if (hasXorTransform) {
      for (let increment = -8; increment <= 8; increment += 1) {
        if (increment === 0) continue;
        candidates.push({
          value: Array.from(cipherText, (character, index) =>
            String.fromCharCode(
              character.charCodeAt(0) ^
                ((key + increment * index) & 0xffff),
            ),
          ).join(""),
          transform: "constant-call XOR",
        });
      }
    }
  }
  for (const key of hasXorTransform ? stringKeys : []) {
    candidates.push({
      value: repeatingXorCharacters(cipherText, key),
      transform: "constant-call XOR",
    });
  }
  if (!integerKeys.length && targetName) {
    const embeddedKeys = [
      ...new Set(
        target?.instructions
          .map(instructionIntegerLiteral)
          .filter((value): value is number => value !== null) ?? [],
      ),
    ].slice(0, 48);
    for (const key of hasXorTransform ? embeddedKeys : []) {
      candidates.push({
        value: xorCharacters(cipherText, key),
        transform: "constant-call XOR",
      });
    }
  }
  if (
    (/base64|decode/i.test(call.reference ?? "") || nameSignalsDecryptor) &&
    /^[A-Za-z0-9+/_=-]{8,}$/.test(cipherText)
  ) {
    candidates.push({
      value: decodeBase64(cipherText),
      transform: "constant-call Base64",
    });
  }
  if (/^(?:[0-9a-f]{2}){4,}$/i.test(cipherText)) {
    candidates.push({
      value: decodeHex(cipherText),
      transform: "constant-call Base64",
    });
  }
  const best = bestDecodedCandidate(
    cipherText,
    candidates,
    decryptorProven,
  );
  if (!best) return null;
  return {
    value: best.value,
    transform: best.transform,
    method: method.name,
    methodDescriptor: method.descriptor,
    offset: call.offset,
    sequenceStartOffset: argumentInstructions[0].offset,
    patchable: true,
    original: cipherText,
  } satisfies StaticRecoveredString;
}

function javaStringHash(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(index)) | 0;
  }
  return hash;
}

/*
 * Recovers strings encrypted by the call-site-dependent rotating-XOR packer
 * used by current WeedHack loaders. This evaluates only the pure arithmetic
 * transform over constant-pool values and never loads or invokes sample code.
 */
export function decodeCallSiteRotatingXor(
  callerClassName: string,
  argument: number,
  cipherText: string,
) {
  if (cipherText.length < 2 || cipherText.length > 16_384) return null;
  const payloadLength = cipherText.length - 1;
  const seed =
    (javaStringHash(callerClassName) ^ argument ^ 0xbccb1085) | 0;
  const state =
    (seed ^
      cipherText.charCodeAt(0) ^
      Math.imul(payloadLength, -1563512307)) |
    0;
  let decoded = "";
  for (let index = 0; index < payloadLength; index += 1) {
    const shift = (index * 6) & 0xf;
    const mask =
      ((state << shift) | (state >>> (16 - shift))) & 0xffff;
    decoded += String.fromCharCode(
      cipherText.charCodeAt(index + 1) ^ mask,
    );
  }
  if (
    decoded === cipherText ||
    printableRatio(decoded) < 0.88 ||
    !/[A-Za-z0-9]/.test(decoded)
  ) {
    return null;
  }
  return decoded;
}

function decodeSingleCharXorBootstrap(candidate: string) {
  if (
    candidate.length < 12 ||
    candidate.length > 4_096 ||
    Array.from(candidate).filter(
      (character) => character.charCodeAt(0) > 0x7f,
    ).length /
      candidate.length <
      0.6
  ) {
    return null;
  }

  const counts = new Map<number, number>();
  for (let index = 0; index < candidate.length; index += 1) {
    const code = candidate.charCodeAt(index);
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  const candidateKeys = Array.from(counts)
    .filter(([, count]) => count >= 2)
    .map(([code]) => code ^ 1);

  for (const key of candidateKeys.slice(0, 64)) {
    let decoded = "";
    let printable = 0;
    for (let index = 0; index < candidate.length; index += 1) {
      const code = candidate.charCodeAt(index) ^ key;
      decoded += String.fromCharCode(code);
      if (code === 1 || code === 9 || code === 10 || (code >= 32 && code < 127)) {
        printable += 1;
      }
    }
    if (printable / decoded.length < 0.96) continue;
    const parts = decoded.split("\u0001");
    if (
      parts.length >= 3 &&
      /^[A-Za-z0-9_$./[\];-]+$/.test(parts[0]) &&
      /^[A-Za-z0-9_$<>-]+$/.test(parts[1]) &&
      /^\([^)]*\).+/.test(parts[2])
    ) {
      return decoded;
    }
  }
  return null;
}

function usefulStaticRebuild(value: string) {
  return (
    usefulReassembledValue(value) ||
    /^https?:\/\/[a-z0-9.-]+\.[a-z]{2,}(?:[/:?#]|$)/i.test(value) ||
    /"(?:accessToken|sessionToken|token)"\s*:/i.test(value) ||
    /(?:powershell|cmd(?:\.exe)?|javaw\.exe)\s+(?:-|\/)/i.test(value) ||
    /(?:^|[\\/])\.minecraft[\\/].{0,160}(?:account|session|token)/i.test(
      value,
    )
  );
}

/**
 * Single owner for translating the detector's transform vocabulary into the
 * report's.
 *
 * The detector vocabulary (`StaticRecoveredString["transform"]`, nine members)
 * is finer-grained than the reporting vocabulary
 * (`RecoveredExpression["transform"]`, eleven members): the evaluator labels a
 * char[] packer "character-array packing" and a constant Base64 call
 * "constant-call Base64", while the report names those simply
 * "character-array packing" and "Base64".
 *
 * This used to be a duplicated ternary chain, one copy per
 * `recoverStaticStrings` call site. The copy in the nested-archive walk had
 * lost the `character-array packing` and `constant-call Base64` branches, so a
 * char-packed string recovered from a class inside a nested archive was
 * reported as "constant propagation" while the identical string in a top-level
 * class was reported as "character-array packing" -- the report contradicted
 * itself about the same detector output purely because of where the class sat
 * in the archive. Both call sites now delegate here, so the two cannot drift.
 *
 * The translation is a pure function of the detector's own label: it never
 * introduces a technique, a score, or a claim the analysis did not make.
 * Anything the detector does not name specifically keeps the "constant
 * propagation" default it has always had, including the "constant-call
 * character shift" and "static fragment rebuild" labels -- those genuinely are
 * not a distinct reported transform, and collapsing them is the honest
 * reading, not a gap to be papered over.
 */
export function staticRecoveredTransform(
  transform: StaticRecoveredString["transform"],
): RecoveredExpression["transform"] {
  switch (transform) {
    case "byte-array string":
      return "byte-array";
    case "character-array packing":
      return "character-array packing";
    case "invokedynamic metadata XOR":
      return "invokedynamic metadata";
    case "constant-call Base64":
      return "Base64";
    default:
      // "call-site rotating XOR", "constant-call XOR" and "heuristic
      // constant-pool XOR" all report as plain XOR, as does any future XOR
      // detector -- that substring test is deliberate and predates this
      // function.
      return transform.includes("XOR") ? "XOR" : "constant propagation";
  }
}

export function recoverStaticStrings(classInfo: ClassInfo) {
  const recovered: StaticRecoveredString[] = [];
  const seen = new Set<string>();
  const add = (item: StaticRecoveredString) => {
    const key = `${item.transform}\u0000${item.method ?? ""}\u0000${item.value}`;
    if (
      seen.has(key) ||
      recovered.length >= 1_000 ||
      item.value.length > 16_384
    ) {
      return;
    }
    seen.add(key);
    recovered.push(item);
  };

  for (const method of classInfo.methods) {
    for (let index = 2; index < method.instructions.length; index += 1) {
      const instruction = method.instructions[index];
      if (
        instruction.mnemonic !== "invokestatic" ||
        !/\(int, java\.lang\.String\) → java\.lang\.String$/.test(
          instruction.reference ?? "",
        )
      ) {
        continue;
      }
      const numberInstruction = method.instructions[index - 2];
      const stringInstruction = method.instructions[index - 1];
      if (
        typeof numberInstruction.literal !== "number" ||
        typeof stringInstruction.literal !== "string"
      ) {
        continue;
      }
      const value = decodeCallSiteRotatingXor(
        classInfo.name,
        numberInstruction.literal,
        stringInstruction.literal,
      );
      if (!value) continue;
      add({
        value,
        transform: "call-site rotating XOR",
        method: method.name,
        methodDescriptor: method.descriptor,
        offset: instruction.offset,
        sequenceStartOffset: numberInstruction.offset,
        patchable: true,
        original: stringInstruction.literal,
      });
    }
  }

  for (const method of classInfo.methods) {
    for (let index = 0; index < method.instructions.length; index += 1) {
      const recoveredCall = constantCallRecovery(classInfo, method, index);
      if (recoveredCall) add(recoveredCall);
    }
  }

  for (const candidate of classInfo.strings.slice(0, 400)) {
    const value = decodeCharacterPackedString(candidate);
    if (!value) continue;
    add({
      value,
      transform: "character-array packing",
      original: candidate,
    });
  }

  for (const candidate of classInfo.strings.slice(0, 400)) {
    const value = decodeSingleCharXorBootstrap(candidate);
    if (!value) continue;
    add({
      value,
      transform: "invokedynamic metadata XOR",
      original: candidate,
    });
  }

  const classIntegerKeys = [
    ...new Set(
      classInfo.methods
        .flatMap((method) => method.instructions)
        .map(instructionIntegerLiteral)
        .filter((value): value is number => value !== null)
        .map((value) => value & 0xffff),
    ),
  ].slice(0, 96);
  const likelyPlainStarts = [
    "http",
    "java",
    "com.",
    "net.",
    "org.",
    "dev.",
    "token",
    "uuid",
    "user",
    "mine",
    "/api",
    "{\"",
  ];
  for (const candidate of classInfo.strings.slice(0, 400)) {
    if (
      candidate.length < 4 ||
      candidate.length > 4_096 ||
      decodedTextScore(candidate) >= 64
    ) {
      continue;
    }
    const derivedKeys = likelyPlainStarts.map(
      (prefix) => candidate.charCodeAt(0) ^ prefix.charCodeAt(0),
    );
    const keys = [...new Set([...classIntegerKeys, ...derivedKeys])].slice(
      0,
      160,
    );
    const best = bestDecodedCandidate(
      candidate,
      keys.map((key) => ({
        value: xorCharacters(candidate, key),
        transform: "heuristic constant-pool XOR" as const,
      })),
    );
    if (!best || decodedTextScore(best.value) < 84) continue;
    add({
      value: best.value,
      transform: best.transform,
      original: candidate,
    });
  }

  for (const method of classInfo.methods) {
    for (let start = 0; start < method.instructions.length; start += 1) {
      const allocation = method.instructions[start];
      if (
        allocation.mnemonic !== "newarray" ||
        !/\bbyte\b/i.test(allocation.operands)
      ) {
        continue;
      }
      const length =
        start > 0
          ? instructionIntegerLiteral(method.instructions[start - 1])
          : null;
      if (length === null || length < 4 || length > 16_384) continue;
      const bytes = new Uint8Array(length);
      const written = new Set<number>();
      let cursor = start + 1;
      for (
        ;
        cursor + 3 < method.instructions.length &&
        cursor < start + Math.min(length * 6 + 24, 4_000);
        cursor += 1
      ) {
        const current = method.instructions[cursor];
        if (
          current.mnemonic === "invokespecial" &&
          /java[/.]lang[/.]String\.<init>/i.test(current.reference ?? "")
        ) {
          break;
        }
        if (current.mnemonic !== "dup") continue;
        const index = instructionIntegerLiteral(
          method.instructions[cursor + 1],
        );
        const value = instructionIntegerLiteral(
          method.instructions[cursor + 2],
        );
        const store = method.instructions[cursor + 3];
        if (
          index === null ||
          value === null ||
          index < 0 ||
          index >= length ||
          store.mnemonic !== "bastore"
        ) {
          continue;
        }
        bytes[index] = value & 0xff;
        written.add(index);
        cursor += 3;
      }
      if (written.size < length * 0.8) continue;
      const value = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
      if (printableRatio(value) < 0.86 || !/[A-Za-z0-9]/.test(value)) continue;
      add({
        value,
        transform: "byte-array string",
        method: method.name,
        offset: allocation.offset,
        original: `${length} byte constants`,
      });
    }
  }

  const byMethod = new Map<string, StaticRecoveredString[]>();
  for (const item of recovered) {
    if (item.transform !== "call-site rotating XOR" || !item.method) continue;
    const values = byMethod.get(item.method) ?? [];
    values.push(item);
    byMethod.set(item.method, values);
  }
  for (const [method, values] of byMethod) {
    values.sort((left, right) => (left.offset ?? 0) - (right.offset ?? 0));
    for (let start = 0; start < values.length; start += 1) {
      let combined = values[start].value;
      for (
        let end = start + 1;
        end < Math.min(values.length, start + 8);
        end += 1
      ) {
        if ((values[end].offset ?? 0) - (values[end - 1].offset ?? 0) > 100) {
          break;
        }
        combined += values[end].value;
        if (!usefulStaticRebuild(combined)) continue;
        add({
          value: combined,
          transform: "static fragment rebuild",
          method,
          offset: values[start].offset,
          original: values
            .slice(start, end + 1)
            .map((item) => item.original)
            .join(""),
        });
      }
    }
  }
  return recovered;
}

export function decodeBase64(candidate: string) {
  try {
    const normalized = candidate
      .replaceAll("-", "+")
      .replaceAll("_", "/")
      .padEnd(Math.ceil(candidate.length / 4) * 4, "=");
    const binary = globalThis.atob(normalized);
    if (binary.length < 6 || binary.length > 16_384) return null;
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return printableRatio(decoded) >= 0.78 ? decoded.trim() : null;
  } catch {
    return null;
  }
}

export function decodeHex(candidate: string) {
  if (candidate.length % 2 || candidate.length < 16) return null;
  try {
    const bytes = new Uint8Array(candidate.length / 2);
    for (let index = 0; index < candidate.length; index += 2) {
      bytes[index / 2] = Number.parseInt(candidate.slice(index, index + 2), 16);
    }
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return printableRatio(decoded) >= 0.82 ? decoded.trim() : null;
  } catch {
    return null;
  }
}

export function decodeXorPackedString(candidate: string) {
  if (candidate.length < 7 || candidate.length > 16_384) return null;
  if (
    Array.from(candidate).some((character) => character.charCodeAt(0) > 0xff)
  ) {
    return null;
  }

  const bytes = Uint8Array.from(candidate, (character) =>
    character.charCodeAt(0),
  );
  const keyLength = bytes[0];
  const payloadLength = bytes.length - 1 - keyLength;
  // Ordinary text keeps passing this shape by accident: the first byte reads as
  // a long key, leaving a few bytes that XOR into short printable junk. A real
  // packet carries more payload than key, and carries something worth hiding.
  if (
    keyLength < 2 ||
    keyLength > 32 ||
    payloadLength < 6 ||
    payloadLength < keyLength
  ) {
    return null;
  }

  const decodedBytes = new Uint8Array(payloadLength);
  for (let index = 0; index < payloadLength; index += 1) {
    decodedBytes[index] =
      bytes[1 + keyLength + index] ^ bytes[1 + (index % keyLength)];
  }

  try {
    const decoded = new TextDecoder("utf-8", { fatal: true }).decode(
      decodedBytes,
    );
    if (
      printableRatio(decoded) < 0.9 ||
      !/[A-Za-z0-9]/.test(decoded) ||
      decoded === candidate
    ) {
      return null;
    }
    return decoded;
  } catch {
    return null;
  }
}

export function decodeCharacterPackedString(candidate: string) {
  if (candidate.length < 5 || candidate.length > 16_384) return null;
  const nonAscii = Array.from(candidate).filter(
    (character) => character.charCodeAt(0) > 0x7f,
  ).length;
  if (nonAscii / candidate.length < 0.35) return null;

  const candidates: string[] = [];
  for (const keyAtEnd of [false, true]) {
    const keyLength = candidate.charCodeAt(
      keyAtEnd ? candidate.length - 1 : 0,
    );
    const payloadLength = candidate.length - keyLength - 1;
    if (
      keyLength < 2 ||
      keyLength > 32 ||
      payloadLength < 3 ||
      payloadLength > 4_096
    ) {
      continue;
    }

    for (const operation of ["xor", "subtract"] as const) {
      let decoded = "";
      for (let index = 0; index < payloadLength; index += 1) {
        const payload = candidate.charCodeAt(
          keyAtEnd ? index : 1 + keyLength + index,
        );
        const key = candidate.charCodeAt(
          keyAtEnd
            ? payloadLength + (index % keyLength)
            : 1 + (index % keyLength),
        );
        decoded += String.fromCharCode(
          operation === "xor" ? payload ^ key : (payload - key) & 0xffff,
        );
      }
      if (
        printableRatio(decoded) >= 0.9 &&
        /[A-Za-z]{2}/.test(decoded) &&
        decodedTextScore(decoded) >= 68
      ) {
        candidates.push(decoded);
      }
    }
  }

  return (
    candidates.sort(
      (left, right) => decodedTextScore(right) - decodedTextScore(left),
    )[0] ?? null
  );
}

export function usefulReassembledValue(value: string) {
  if (value.length < 8 || value.length > 4_096) return false;
  if (/^0x[0-9a-f]{32,64}$/i.test(value)) return true;
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value,
    )
  ) {
    return true;
  }
  if (
    (value.startsWith("{") && value.endsWith("}")) ||
    (value.startsWith("[") && value.endsWith("]"))
  ) {
    return true;
  }
  if (/^\/(?:shard|cdn)\//i.test(value)) return true;
  if (
    /^(?:X-(?:Runtime|Edge|CDN)-|(?:_stealer|_spawn)\.log$)/i.test(value)
  ) {
    return true;
  }
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    const url = new URL(value);
    return (
      /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(url.hostname) &&
      !/https?:\/\//i.test(value.slice(value.indexOf("://") + 3))
    );
  } catch {
    return false;
  }
}
